import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  icon?: IconProp;
  children: React.ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl";
};

export const Modal = ({
  isOpen,
  onClose,
  title,
  icon,
  children,
  maxWidth = "md",
}: ModalProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const maxWidthClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    // Ajout (2026-09-21) pour les écrans denses type gestion de licences : `xl` plafonne
    // à 576px, ce qui allonge les lignes et réduit d'autant le nombre d'éléments visibles.
    // Les tailles existantes sont inchangées.
    "2xl": "max-w-2xl",
  };

  if (!isOpen) return null;

  // Rendu dans <body> : ouvert depuis une fiche (TableDetails, qui a une `transform`), un
  // `fixed` se calait sur la fiche et non sur l'écran, et l'ombre s'arrêtait au défilement.
  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out ${
          isVisible ? "opacity-25" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto mb-16">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <div
            className={`w-full ${
              maxWidthClasses[maxWidth]
            } transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all duration-300 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 -translate-y-4 scale-95"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              {icon && <FontAwesomeIcon icon={icon} />}
              {title && (
                <h3 className="text-lg font-oxaniumMedium text-tertiary">
                  {title}
                </h3>
              )}
              <button
                onClick={handleClose}
                className="text-grey-medium hover:text-tertiary transition-colors"
              >
                <FontAwesomeIcon icon="times" className="h-5 w-5" />
              </button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Composants complémentaires pour les modals
export const ModalActions = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-6 flex justify-end gap-4">{children}</div>
);
